/**
 * Shared presentational pieces of an SMS-recovery thread — the Extraction pane
 * (card image + extracted fields) and the Conversation thread (message bubbles).
 *
 * Two slideouts render these with different data plumbing:
 *  - RecoveryLoopSlideout (tenant app): live Firestore subscription + the
 *    clinic-scoped getRecoveryCardImage callable.
 *  - SmsActivitySlideout (staff console, cross-org, no impersonation): the
 *    getSmsThreadForStaff callable + the staff-gated getCardImage callable.
 *
 * Non-component helpers (the image-fetch hook, formatters) live in
 * utils/smsThread.ts. Styling reuses the `rl-*` classes in Dashboard.css
 * (bundled globally).
 */
import { Fragment, useState } from 'react'
import type { RecoveryExtraction, RecoveryMessage, StatedField } from '../services/recoveryCases'
import { pickCaseCardMedia } from '../../shared/recoveryDrawer'
import { deliveryFailureNote } from '../utils/recoveryBand'
import { mediaKey, fmtMsgTime, type CardUrls, type CaseCardImage } from '../utils/smsThread'
import { buildCardDetailRows } from '../utils/cardDetailRows'
import type { CorrectableField } from '../utils/checkCorrection'
import type { CardCorrection } from '../hooks/useCardCorrection'
import { useBrowserBackSentinel } from '../utils/useBrowserBackSentinel'
import CardImageViewer from './CardImageViewer'
import CardDetailRows from './CardDetailRows'
import { IconSparkle, IconRotate } from './Icons'

interface ExtractionPaneProps {
  cardUrl: string | null
  /** EVERY image attributed to this case, newest first (#309). The patient
   *  sent them; staff can see them. Falls back to `cardUrl` when a caller has
   *  not been updated. */
  cardImages?: CaseCardImage[]
  /** DOB we already hold from the record. A card that doesn't print one is
   *  then not a question, and must not raise a review flag (#309). */
  recordDob?: string | null
  /** Shown inside the empty card frame when no image has loaded. */
  noCardNote: string
  extraction: RecoveryExtraction | null
  /** cannot_extract — renders the "illegible card" callout. */
  illegible: boolean
  /** Shown when there's no extraction (and the case isn't illegible). */
  emptyNote: string | null
  /** The payer the failed check actually queried. When the card names a
   *  different one, the re-run substitutes it (#267) and the Payer row is
   *  marked as AI-suggested so staff can see the check was re-routed. */
  recordPayerName?: string | null
  /** The case's patient-texted answers, so a value the patient typed is not
   *  presented as something the card printed (the merged read cannot tell them
   *  apart on its own). */
  statedFields?: Partial<Record<StatedField, string>> | null
  /**
   * Makes the rows beside the enlarged card editable (#349).
   *
   * The controller is owned by the HOST, not built here: switching to the
   * Conversation tab unmounts this pane, and a draft living inside it would be
   * discarded by a tab switch — the silent discard #274 exists to prevent.
   *
   * Passed by the tenant Recovery drawer, which has the org, the check and
   * write access. Omitted by the staff console: it is cross-org with no
   * impersonation, so its caller has no org to correct a check in.
   */
  correction?: CardCorrection | null
  /**
   * What the check ASKED the payer — the rows' value where it has one (#349).
   *
   * Supplied by the tenant drawer, which holds the check. Omitted by the staff
   * console, whose row payload carries no `requestSubscriber`; its rows then
   * fall back to the card read, which is what they showed before. Worth closing
   * once the staff payload carries the asked values too.
   */
  asked?: Partial<Record<CorrectableField, string>> | null
}

/**
 * One card image, with what we know about it and a manual rotate (#309).
 *
 * The pipeline decides orientation deliberately and verifies it by probe, but
 * that decision is stored for the newest read only — so rather than claim an
 * orientation per image we cannot prove, every image gets a rotate control and
 * the label states only what is actually known: which of the set it is and when
 * it arrived. Rotation is view-only; it changes nothing stored.
 */
function CardImageFrame({
  image, index, total, onEnlarge,
}: { image: CaseCardImage; index: number; total: number; onEnlarge: () => void }) {
  const [deg, setDeg] = useState(0)
  const sideways = deg === 90 || deg === 270
  return (
    <figure className="rl-card">
      {/* #327: the thumbnail is a BUTTON into the full-screen viewer — the
          member ID staff are asked to confirm is a few pixels tall at this
          size, and a click that does nothing teaches them to rubber-stamp. */}
      <button
        type="button"
        className={`rl-card__frame rl-card__open${sideways ? ' rl-card__frame--sideways' : ''}`}
        onClick={onEnlarge}
        title="Click to enlarge"
        aria-label={`Enlarge insurance card image ${index + 1} of ${total}`}
      >
        <img
          className="rl-card__img"
          src={image.url}
          alt={`Insurance card image ${index + 1} of ${total}`}
          style={{ transform: `rotate(${deg}deg)` }}
        />
      </button>
      <figcaption className="rl-card__meta">
        <span>
          {total > 1 ? `Image ${index + 1} of ${total}` : 'Card image'}
          {image.receivedAt ? ` · ${fmtMsgTime(image.receivedAt)}` : ''}
        </span>
        <button
          type="button"
          className="rl-card__rotate"
          onClick={() => setDeg(d => (d + 90) % 360)}
          title="Rotate this view (does not change the stored image)"
          aria-label="Rotate this view"
        >
          <IconRotate />
        </button>
      </figcaption>
    </figure>
  )
}

/** Card image + the extracted-fields table + the illegible/empty callouts. */
export function ExtractionPane({
  cardUrl, cardImages, recordDob, noCardNote, extraction, illegible, emptyNote,
  recordPayerName, statedFields, correction, asked,
}: ExtractionPaneProps) {
  // #309: show every image the patient sent, not just the newest. Callers that
  // still pass only `cardUrl` degrade to the old single-image behaviour rather
  // than rendering nothing.
  const images: CaseCardImage[] = cardImages && cardImages.length > 0
    ? cardImages
    : cardUrl
      ? [{ url: cardUrl, intakeId: 'legacy', objectIndex: 0, receivedAt: null }]
      : []
  // #327: which image the full-screen viewer is open on; null = closed.
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)
  // Browser Back closes the viewer (#349 A11). Registered HERE, in the pane
  // that stays mounted across the close, so an in-app close (Escape, ✕) gives
  // the history slot back — a sentinel inside the viewer would strand it on
  // every close, and the next Back press would do nothing (#231).
  useBrowserBackSentinel(viewerIndex !== null, () => setViewerIndex(null))
  // The rows live in buildCardDetailRows (shared with the viewer, #327): the
  // #309 three-state field reads and the #267 substitution badge, unchanged.
  const detailRows = buildCardDetailRows({ extraction, recordDob, recordPayerName, statedFields, asked })

  return (
    <>
      <div className="rl-section-label">Insurance card</div>
      {images.length > 0 ? (
        <div className="rl-cards">
          {images.map((img, i) => (
            <CardImageFrame
              key={`${img.intakeId}:${img.objectIndex}`}
              image={img}
              index={i}
              total={images.length}
              onEnlarge={() => setViewerIndex(i)}
            />
          ))}
        </div>
      ) : (
        <div className="rl-card-frame">
          <div className="rl-card-frame__empty">{noCardNote}</div>
        </div>
      )}

      {extraction && (
        <>
          <div className="rl-section-label">Extracted details</div>
          {/* #349: the rows ARE the editing surface. What used to sit under
              them — a "Wrong payer? Correct it for this clinic" link — is gone
              on purpose, and not because it duplicated a field: it wrote a
              CLINIC-WIDE payer alias, a rule for every future patient at that
              practice, from the screen where someone is fixing one visit. If a
              clinic needs that mapping it gets made deliberately on the Payer
              Mappings page. Correcting the payer here corrects THIS check. */}
          <CardDetailRows rows={detailRows} correction={correction} />
        </>
      )}

      {illegible && (
        <div className="rl-callout rl-callout--error">
          <strong>Illegible card — routed to staff</strong>
          <p>
            The card couldn't be read clearly, so nothing was applied. The automated loop is
            exhausted — request a clearer photo or call the patient.
          </p>
        </div>
      )}

      {!extraction && !illegible && emptyNote && (
        <div className="rl-empty-note">{emptyNote}</div>
      )}

      {/* #327: the full-screen viewer, over the SAME image list the frames
          render — the fields ride along so a member ID is compared digit by
          digit against the photo, not against memory. */}
      {viewerIndex !== null && images.length > 0 && (
        <CardImageViewer
          images={images}
          initialIndex={Math.min(viewerIndex, images.length - 1)}
          fields={extraction ? detailRows : null}
          correction={extraction ? correction : null}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </>
  )
}

interface ConversationThreadProps {
  messages: RecoveryMessage[]
  cardUrls: CardUrls
  /** Trailing note under the thread (e.g. the illegible-card routing note). */
  footnote?: string | null
  /**
   * Case context for the full-screen viewer (#327): the same extracted rows the
   * Extraction pane shows ride beside the enlarged photo, so the comparison
   * doesn't depend on which tab the click came from. Optional — a caller with
   * no case in scope opens the viewer image-only.
   */
  extraction?: RecoveryExtraction | null
  recordDob?: string | null
  recordPayerName?: string | null
  statedFields?: Partial<Record<StatedField, string>> | null
  /**
   * The case whose `extraction` this is (its checkId). Required for the fields
   * to ride along with a photo: the thread is THREAD-scoped and one phone can
   * carry several concurrent cases, so without it there is no way to tell which
   * photos the rows actually describe. Omitted ⇒ the viewer opens image-only.
   */
  caseId?: string | null
  /**
   * The same two inputs the Extraction pane gives its rows (#349), so the
   * viewer is ONE viewer whichever tab the click came from. Without them the
   * thread's copy showed the card read, read-only, and after a correction the
   * two tabs disagreed about the same fields. The staff console passes neither
   * and stays read-only; a photo outside the case still opens image-only.
   */
  asked?: Partial<Record<CorrectableField, string>> | null
  correction?: CardCorrection | null
}

/** The message thread — outbound/inbound bubbles with inline card photos. */
export function ConversationThread({
  messages, cardUrls, footnote, extraction, recordDob, recordPayerName,
  statedFields, caseId, asked, correction,
}: ConversationThreadProps) {
  // #327: a photo in the thread opens the SAME viewer as the Extraction pane's
  // card frames. The viewer's list is every photo that has loaded, in thread
  // order — "every image on the case" from wherever the click happened.
  const photos: CaseCardImage[] = []
  for (const m of messages) {
    for (const media of m.media) {
      const url = cardUrls[mediaKey(media.intakeId, media.objectIndex)]
      if (!url) continue
      photos.push({
        url,
        intakeId: media.intakeId,
        objectIndex: media.objectIndex,
        receivedAt: m.occurredAt ?? m.createdAt,
      })
    }
  }
  const [viewerKey, setViewerKey] = useState<string | null>(null)
  const viewerIndex = viewerKey === null
    ? -1
    : photos.findIndex((p) => mediaKey(p.intakeId, p.objectIndex) === viewerKey)
  // Browser Back closes the viewer — owned by the thread for the same reason
  // the Extraction pane owns its copy: the viewer unmounts on close, and only a
  // sentinel that outlives the close can reclaim the history slot (#349 A11).
  useBrowserBackSentinel(viewerIndex >= 0, () => setViewerKey(null))
  const viewerFields = extraction
    ? buildCardDetailRows({ extraction, recordDob, recordPayerName, statedFields, asked })
    : null
  // Which of the thread's photos those rows actually describe. Same selector
  // the Extraction pane uses (`pickCaseCardMedia`), so the two panes cannot
  // disagree about which images belong to the case — attributed photos win,
  // and only a case with none falls back to still-unattributed ones.
  const fieldsMediaKeys = caseId
    ? new Set(pickCaseCardMedia(messages, caseId).map((m) => mediaKey(m.intakeId, m.objectIndex)))
    : new Set<string>()
  return (
    <div className="rl-thread">
      {messages.length === 0 && (
        <div className="rl-empty-note">No messages yet.</div>
      )}
      {messages.map((m) => {
        const failure = deliveryFailureNote(m)
        return (
        <Fragment key={m.id}>
        <div
          className={`rl-msg rl-msg--${m.direction === 'outbound' ? 'out' : 'in'}`}
        >
          <div className={`rl-msg__bubble${m.media.length > 0 && !m.text ? ' rl-msg__bubble--media' : ''}`}>
            {m.text}
            {m.media.map((media) => {
              const key = mediaKey(media.intakeId, media.objectIndex)
              const url = cardUrls[key]
              return url
                ? <button key={key} type="button" className="rl-msg__photo-btn"
                  onClick={() => setViewerKey(key)} title="Click to enlarge"
                  aria-label="Enlarge card photo">
                  <img className="rl-msg__photo" src={url} alt="Card photo" />
                </button>
                : <div key={key}
                  className="rl-msg__photo rl-msg__photo--placeholder">Photo</div>
            })}
            <span className="rl-msg__time">{fmtMsgTime(m.occurredAt ?? m.createdAt)}</span>
          </div>
          {/* Automated (agent-composed) sends carry the loop's AI marker (#269) —
              same wording as the Cockpit's payer-suggestion badge. */}
          {m.direction === 'outbound' && m.aiIntent && (
            <span className="rl-msg__ai"><IconSparkle /> Suggested by VitalRev AI</span>
          )}
        </div>
        {/* A send that never landed says so right where staff are reading, with
            the carrier's reason and the next step — not buried in fine print. */}
        {failure && <div className="rl-thread__sys">{failure}</div>}
        </Fragment>
        )
      })}
      {footnote && (
        <div className="rl-thread__note">{footnote}</div>
      )}
      {viewerIndex >= 0 && (
        <CardImageViewer
          images={photos}
          initialIndex={viewerIndex}
          fields={viewerFields}
          fieldsMediaKeys={fieldsMediaKeys}
          correction={extraction ? correction : null}
          onClose={() => setViewerKey(null)}
        />
      )}
    </div>
  )
}
